'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HeightCalculator() {
  const t = useTranslations("tools.heightCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator 1 state (Child Height Prediction)
  const [calc1Unit, setCalc1Unit] = useState<'imperial' | 'metric'>('imperial');
  const [calc1Inputs, setCalc1Inputs] = useState({
    childAge: 5,
    childGender: 'male' as 'male' | 'female',
    childFeet: 3,
    childInches: 8,
    childHeightCm: 112,
    childWeightLbs: 40,
    childWeightKg: 18,
    motherFeet: 5,
    motherInches: 5,
    motherHeightCm: 165,
    fatherFeet: 5,
    fatherInches: 10,
    fatherHeightCm: 178,
  });

  // Calculator 2 state (Parents' Height Only)
  const [calc2Unit, setCalc2Unit] = useState<'imperial' | 'metric'>('imperial');
  const [calc2Inputs, setCalc2Inputs] = useState({
    motherFeet: 5,
    motherInches: 2,
    motherHeightCm: 157,
    fatherFeet: 5,
    fatherInches: 10,
    fatherHeightCm: 178,
  });

  // Results state
  const [results, setResults] = useState<{
    boy: number;
    girl: number;
  } | null>(null);

  // Converter state
  const [convertFeet, setConvertFeet] = useState(5);
  const [convertInches, setConvertInches] = useState(10);
  const [convertCm, setConvertCm] = useState(178);
  const [metricResult, setMetricResult] = useState('');
  const [imperialResult, setImperialResult] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Conversion functions
  const feetInchesToCm = (feet: number, inches: number): number => {
    return (feet * 30.48) + (inches * 2.54);
  };

  const cmToFeetInches = (cm: number): { feet: number; inches: number } => {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return { feet, inches };
  };

  // Height prediction calculation
  const calculatePredictedHeight = (
    motherHeightCm: number,
    fatherHeightCm: number,
    childAge?: number,
    childHeightCm?: number,
  ): { boy: number; girl: number } => {
    const midParentHeight = (motherHeightCm + fatherHeightCm) / 2;

    let predictedBoy = midParentHeight + 6.5;
    let predictedGirl = midParentHeight - 6.5;

    if (childAge && childHeightCm) {
      const growthFactor = childAge < 12 ? 1.1 : 1.05;
      const currentAdjustment = (childHeightCm - (midParentHeight * (childAge / 18))) * growthFactor;

      predictedBoy += currentAdjustment * 0.3;
      predictedGirl += currentAdjustment * 0.3;
    }

    return {
      boy: Math.max(predictedBoy, 150),
      girl: Math.max(predictedGirl, 140)
    };
  };

  // Calculator 1 submit handler
  const handleCalc1Submit = (e: React.FormEvent) => {
    e.preventDefault();

    let motherHeightCm: number;
    let fatherHeightCm: number;
    let childHeightCm: number;

    if (calc1Unit === 'imperial') {
      motherHeightCm = feetInchesToCm(calc1Inputs.motherFeet, calc1Inputs.motherInches);
      fatherHeightCm = feetInchesToCm(calc1Inputs.fatherFeet, calc1Inputs.fatherInches);
      childHeightCm = feetInchesToCm(calc1Inputs.childFeet, calc1Inputs.childInches);
    } else {
      motherHeightCm = calc1Inputs.motherHeightCm;
      fatherHeightCm = calc1Inputs.fatherHeightCm;
      childHeightCm = calc1Inputs.childHeightCm;
    }

    const predicted = calculatePredictedHeight(
      motherHeightCm,
      fatherHeightCm,
      calc1Inputs.childAge,
      childHeightCm
    );

    setResults(predicted);
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  // Calculator 2 submit handler
  const handleCalc2Submit = (e: React.FormEvent) => {
    e.preventDefault();

    let motherHeightCm: number;
    let fatherHeightCm: number;

    if (calc2Unit === 'imperial') {
      motherHeightCm = feetInchesToCm(calc2Inputs.motherFeet, calc2Inputs.motherInches);
      fatherHeightCm = feetInchesToCm(calc2Inputs.fatherFeet, calc2Inputs.fatherInches);
    } else {
      motherHeightCm = calc2Inputs.motherHeightCm;
      fatherHeightCm = calc2Inputs.fatherHeightCm;
    }

    const predicted = calculatePredictedHeight(motherHeightCm, fatherHeightCm);

    setResults(predicted);
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  // Converter handlers
  const handleConvertToMetric = () => {
    const cm = feetInchesToCm(convertFeet, convertInches);
    setMetricResult(`${convertFeet}' ${convertInches}" = ${cm.toFixed(2)} cm`);
  };

  const handleConvertToImperial = () => {
    const result = cmToFeetInches(convertCm);
    setImperialResult(`${convertCm} cm = ${result.feet}' ${result.inches.toFixed(1)}"`);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '900px', margin: '0 auto' },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const toolH2Style = getResponsiveStyle(
    { fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: '#64748b' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const calculatorGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const cardTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#1e293b', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' }
  );

  const unitTabsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    background: '#f1f5f9',
    padding: '0.25rem',
    borderRadius: '0.5rem',
  };

  const unitTabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '0.5rem 1rem',
    border: 'none',
    background: isActive ? 'white' : 'transparent',
    color: isActive ? '#2563eb' : '#64748b',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '0.375rem',
    transition: 'all 0.2s',
    boxShadow: isActive ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
  });

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1.25rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 500, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const inputRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
  };

  const inputWrapperStyle: React.CSSProperties = {
    position: 'relative',
    flex: 1,
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.2s',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.8rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const inputUnitStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontSize: '0.875rem',
    pointerEvents: 'none',
  };

  const radioGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem',
  };

  const radioLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontWeight: 400,
  };

  const radioInputStyle: React.CSSProperties = {
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '1rem 1.5rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginTop: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#1e293b', marginBottom: '1.5rem', textAlign: 'center' as const },
    { fontSize: '1.25rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle: React.CSSProperties = {
    display: 'grid',
    gap: '1rem',
  };

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb',
    },
    { padding: '1rem' },
    { padding: '1.15rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500,
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.5rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.75rem' }
  );

  const resultNoteStyle = getResponsiveStyle(
    {
      marginTop: '1rem',
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      borderRadius: '0.5rem',
      color: '#92400e',
    },
    { padding: '0.875rem', fontSize: '0.85rem' },
    { padding: '0.95rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const converterSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginTop: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const converterH3Style = getResponsiveStyle(
    { color: '#1e293b', marginBottom: '1.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' }
  );

  const converterH4Style = getResponsiveStyle(
    { color: '#64748b', marginBottom: '1rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const converterRowStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1.5rem' },
    { flexDirection: 'column' as const, alignItems: 'stretch' as const },
    { flexDirection: 'row' as const, alignItems: 'flex-end' as const },
    { flexDirection: 'row' as const, alignItems: 'flex-end' as const }
  );

  const converterFormGroupStyle: React.CSSProperties = {
    flex: 1,
    marginBottom: 0,
  };

  const convertBtnStyle = getResponsiveStyle(
    {
      background: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap' as const,
    },
    { padding: '0.75rem 1.5rem', width: '100%' },
    { padding: '0.75rem 1.5rem', width: 'auto' },
    { padding: '0.75rem 1.5rem', width: 'auto' }
  );

  const converterResultStyle: React.CSSProperties = {
    padding: '1rem',
    background: '#f0fdf4',
    borderRadius: '0.5rem',
    color: '#166534',
    fontWeight: 600,
    textAlign: 'center',
    marginTop: '0.5rem',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const strongStyle: React.CSSProperties = {
    color: colors.primary,
    fontWeight: 600,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolH2Style}>📏 {t("toolTitle")}</h2>
            <p style={toolSubtitleStyle}>{t("toolSubtitle")}</p>
          </div>

          <div style={calculatorGridStyle}>
            {/* Calculator 1: Child Height Prediction */}
            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>👶 {t("calc1Title")}</h3>

              <div style={unitTabsStyle}>
                <button
                  type="button"
                  style={unitTabStyle(calc1Unit === 'imperial')}
                  onClick={() => setCalc1Unit('imperial')}
                >
                  {t("usUnits")}
                </button>
                <button
                  type="button"
                  style={unitTabStyle(calc1Unit === 'metric')}
                  onClick={() => setCalc1Unit('metric')}
                >
                  {t("metricUnits")}
                </button>
              </div>

              <form onSubmit={handleCalc1Submit}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("childAge")}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="18"
                    value={calc1Inputs.childAge}
                    onChange={(e) => setCalc1Inputs({...calc1Inputs, childAge: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("childGender")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="child-gender"
                        value="male"
                        checked={calc1Inputs.childGender === 'male'}
                        onChange={(e) => setCalc1Inputs({...calc1Inputs, childGender: e.target.value as 'male' | 'female'})}
                        style={radioInputStyle}
                      />
                      {t("male")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="child-gender"
                        value="female"
                        checked={calc1Inputs.childGender === 'female'}
                        onChange={(e) => setCalc1Inputs({...calc1Inputs, childGender: e.target.value as 'male' | 'female'})}
                        style={radioInputStyle}
                      />
                      {t("female")}
                    </label>
                  </div>
                </div>

                {calc1Unit === 'imperial' ? (
                  <>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("childHeight")}</label>
                      <div style={inputRowStyle}>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="8"
                            value={calc1Inputs.childFeet}
                            onChange={(e) => setCalc1Inputs({...calc1Inputs, childFeet: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>ft</span>
                        </div>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="11"
                            step="0.1"
                            value={calc1Inputs.childInches}
                            onChange={(e) => setCalc1Inputs({...calc1Inputs, childInches: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>in</span>
                        </div>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("childWeight")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={calc1Inputs.childWeightLbs}
                          onChange={(e) => setCalc1Inputs({...calc1Inputs, childWeightLbs: parseFloat(e.target.value) || 0})}
                          style={inputStyle}
                          required
                        />
                        <span style={inputUnitStyle}>lbs</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("motherHeight")}</label>
                      <div style={inputRowStyle}>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="8"
                            value={calc1Inputs.motherFeet}
                            onChange={(e) => setCalc1Inputs({...calc1Inputs, motherFeet: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>ft</span>
                        </div>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="11"
                            step="0.1"
                            value={calc1Inputs.motherInches}
                            onChange={(e) => setCalc1Inputs({...calc1Inputs, motherInches: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>in</span>
                        </div>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("fatherHeight")}</label>
                      <div style={inputRowStyle}>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="8"
                            value={calc1Inputs.fatherFeet}
                            onChange={(e) => setCalc1Inputs({...calc1Inputs, fatherFeet: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>ft</span>
                        </div>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="11"
                            step="0.1"
                            value={calc1Inputs.fatherInches}
                            onChange={(e) => setCalc1Inputs({...calc1Inputs, fatherInches: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>in</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("childHeight")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={calc1Inputs.childHeightCm}
                          onChange={(e) => setCalc1Inputs({...calc1Inputs, childHeightCm: parseFloat(e.target.value) || 0})}
                          style={inputStyle}
                          required
                        />
                        <span style={inputUnitStyle}>cm</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("childWeight")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={calc1Inputs.childWeightKg}
                          onChange={(e) => setCalc1Inputs({...calc1Inputs, childWeightKg: parseFloat(e.target.value) || 0})}
                          style={inputStyle}
                          required
                        />
                        <span style={inputUnitStyle}>kg</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("motherHeight")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={calc1Inputs.motherHeightCm}
                          onChange={(e) => setCalc1Inputs({...calc1Inputs, motherHeightCm: parseFloat(e.target.value) || 0})}
                          style={inputStyle}
                          required
                        />
                        <span style={inputUnitStyle}>cm</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("fatherHeight")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={calc1Inputs.fatherHeightCm}
                          onChange={(e) => setCalc1Inputs({...calc1Inputs, fatherHeightCm: parseFloat(e.target.value) || 0})}
                          style={inputStyle}
                          required
                        />
                        <span style={inputUnitStyle}>cm</span>
                      </div>
                    </div>
                  </>
                )}

                <button type="submit" style={calculateBtnStyle}>
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {/* Calculator 2: Parents' Height Only */}
            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>👨‍👩‍👧 {t("calc2Title")}</h3>

              <div style={unitTabsStyle}>
                <button
                  type="button"
                  style={unitTabStyle(calc2Unit === 'imperial')}
                  onClick={() => setCalc2Unit('imperial')}
                >
                  {t("usUnits")}
                </button>
                <button
                  type="button"
                  style={unitTabStyle(calc2Unit === 'metric')}
                  onClick={() => setCalc2Unit('metric')}
                >
                  {t("metricUnits")}
                </button>
              </div>

              <form onSubmit={handleCalc2Submit}>
                {calc2Unit === 'imperial' ? (
                  <>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("motherHeight")}</label>
                      <div style={inputRowStyle}>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="8"
                            value={calc2Inputs.motherFeet}
                            onChange={(e) => setCalc2Inputs({...calc2Inputs, motherFeet: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>ft</span>
                        </div>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="11"
                            step="0.1"
                            value={calc2Inputs.motherInches}
                            onChange={(e) => setCalc2Inputs({...calc2Inputs, motherInches: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>in</span>
                        </div>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("fatherHeight")}</label>
                      <div style={inputRowStyle}>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="8"
                            value={calc2Inputs.fatherFeet}
                            onChange={(e) => setCalc2Inputs({...calc2Inputs, fatherFeet: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>ft</span>
                        </div>
                        <div style={inputWrapperStyle}>
                          <input
                            type="number"
                            min="0"
                            max="11"
                            step="0.1"
                            value={calc2Inputs.fatherInches}
                            onChange={(e) => setCalc2Inputs({...calc2Inputs, fatherInches: parseFloat(e.target.value) || 0})}
                            style={inputStyle}
                            required
                          />
                          <span style={inputUnitStyle}>in</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("motherHeight")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={calc2Inputs.motherHeightCm}
                          onChange={(e) => setCalc2Inputs({...calc2Inputs, motherHeightCm: parseFloat(e.target.value) || 0})}
                          style={inputStyle}
                          required
                        />
                        <span style={inputUnitStyle}>cm</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("fatherHeight")}</label>
                      <div style={inputWrapperStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={calc2Inputs.fatherHeightCm}
                          onChange={(e) => setCalc2Inputs({...calc2Inputs, fatherHeightCm: parseFloat(e.target.value) || 0})}
                          style={inputStyle}
                          required
                        />
                        <span style={inputUnitStyle}>cm</span>
                      </div>
                    </div>
                  </>
                )}

                <button type="submit" style={calculateBtnStyle}>
                  {t("calculateButton")}
                </button>
              </form>
            </div>
          </div>

          {/* Results */}
          {results && (
            <div id="results" style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t("resultsTitle")}</h3>
              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("predictedBoy")}</span>
                  <span style={resultValueStyle}>
                    {`${cmToFeetInches(results.boy).feet}' ${cmToFeetInches(results.boy).inches.toFixed(1)}"`} ({results.boy.toFixed(1)} cm)
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("predictedGirl")}</span>
                  <span style={resultValueStyle}>
                    {`${cmToFeetInches(results.girl).feet}' ${cmToFeetInches(results.girl).inches.toFixed(1)}"`} ({results.girl.toFixed(1)} cm)
                  </span>
                </div>
              </div>
              <div style={resultNoteStyle}>
                <strong>{t("noteLabel")}</strong> {t("noteText")}
              </div>
            </div>
          )}

          {/* Converter */}
          <div style={converterSectionStyle}>
            <h3 style={converterH3Style}>🔄 {t("converterTitle")}</h3>

            <div>
              <h4 style={converterH4Style}>{t("toMetric")}</h4>
              <div style={converterRowStyle}>
                <div style={converterFormGroupStyle}>
                  <label style={labelStyle}>{t("feet")}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={convertFeet}
                    onChange={(e) => setConvertFeet(parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                </div>
                <div style={converterFormGroupStyle}>
                  <label style={labelStyle}>{t("inches")}</label>
                  <input
                    type="number"
                    min="0"
                    max="11"
                    step="0.1"
                    value={convertInches}
                    onChange={(e) => setConvertInches(parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                </div>
                <button type="button" style={convertBtnStyle} onClick={handleConvertToMetric}>
                  {t("convert")}
                </button>
              </div>
              {metricResult && (
                <div style={converterResultStyle}>{metricResult}</div>
              )}
            </div>

            <div style={{ marginTop: '2rem' }}>
              <h4 style={converterH4Style}>{t("toImperial")}</h4>
              <div style={converterRowStyle}>
                <div style={converterFormGroupStyle}>
                  <label style={labelStyle}>{t("centimeters")}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={convertCm}
                    onChange={(e) => setConvertCm(parseFloat(e.target.value) || 0)}
                    style={inputStyle}
                  />
                </div>
                <button type="button" style={convertBtnStyle} onClick={handleConvertToImperial}>
                  {t("convert")}
                </button>
              </div>
              {imperialResult && (
                <div style={converterResultStyle}>{imperialResult}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t("step1Title")}</strong> {t("step1Text")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t("step2Title")}</strong> {t("step2Text")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t("step3Title")}</strong> {t("step3Text")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t("step4Title")}</strong> {t("step4Text")}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t("step5Title")}</strong> {t("step5Text")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t("insight1Title")}</strong> {t("insight1Text")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t("insight2Title")}</strong> {t("insight2Text")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t("insight3Title")}</strong> {t("insight3Text")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t("insight4Title")}</strong> {t("insight4Text")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t("insight5Title")}</strong> {t("insight5Text")}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t("faq1Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t("faq2Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t("faq3Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t("faq4Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>
        <div>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t("faq5Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://learnfast.ai/features/height-estimator-ai" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference1")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.bajajfinserv.in/insurance/height-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference2")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.gigacalculator.com/calculators/height-calculator.php" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference3")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.calculator.net/height-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference4")}
            </a>
          </li>
          <li>
            <a href="https://www.vidnoz.com/ai-solutions/ai-height-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}